import React, { useState } from 'react';
import {
  Truck,
  Package,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Star,
  Filter,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Plus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { australianSuppliers, Supplier } from '../../data/medicationDatabase';

const SupplyChain: React.FC = () => {
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Mock supply chain data
  const supplyChainMetrics = {
    totalSuppliers: 15,
    activeOrders: 23,
    averageLeadTime: 3.2,
    onTimeDelivery: 94.5,
    totalValue: 125000,
    pendingApprovals: 8
  };

  const purchaseOrders = [
    {
      id: 'PO-2024-001',
      supplier: 'Sigma Healthcare',
      orderDate: '2024-09-10',
      expectedDelivery: '2024-09-17',
      status: 'In Transit',
      value: 15420.50,
      items: 45,
      leadTime: 7,
      priority: 'High'
    },
    {
      id: 'PO-2024-002',
      supplier: 'API',
      orderDate: '2024-09-12',
      expectedDelivery: '2024-09-19',
      status: 'Confirmed',
      value: 8750.00,
      items: 28,
      leadTime: 7,
      priority: 'Medium'
    },
    {
      id: 'PO-2024-003',
      supplier: 'Chemist Warehouse Group',
      orderDate: '2024-09-14',
      expectedDelivery: '2024-09-16',
      status: 'Urgent',
      value: 3200.75,
      items: 12,
      leadTime: 2,
      priority: 'Urgent'
    }
  ];

  const stockAlerts = [
    {
      medication: 'Rosuvastatin 20mg',
      currentStock: 15,
      minimumLevel: 50,
      leadTime: 3,
      supplier: 'Sigma Healthcare',
      lastOrderDate: '2024-08-15',
      status: 'Critical'
    },
    {
      medication: 'Pantoprazole 40mg',
      currentStock: 25,
      minimumLevel: 40,
      leadTime: 4,
      supplier: 'API',
      lastOrderDate: '2024-09-01',
      status: 'Low'
    },
    {
      medication: 'Warfarin 5mg',
      currentStock: 8,
      minimumLevel: 30,
      leadTime: 5,
      supplier: 'Sigma Healthcare',
      lastOrderDate: '2024-08-20',
      status: 'Critical'
    }
  ];

  const deliveryPerformance = [
    { month: 'May', onTime: 92, late: 8, early: 0 },
    { month: 'Jun', onTime: 94, late: 5, early: 1 },
    { month: 'Jul', onTime: 96, late: 3, early: 1 },
    { month: 'Aug', onTime: 93, late: 6, early: 1 },
    { month: 'Sep', onTime: 95, late: 4, early: 1 }
  ];

  const supplierPerformance = [
    { name: 'Sigma Healthcare', performance: 96, color: '#10B981' },
    { name: 'API', performance: 94, color: '#3B82F6' },
    { name: 'CWG', performance: 92, color: '#F59E0B' },
    { name: 'Others', performance: 89, color: '#EF4444' }
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in transit': return 'bg-blue-100 text-blue-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'delivered': return 'bg-emerald-100 text-emerald-800';
      case 'delayed': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStockStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'critical': return 'bg-red-500';
      case 'low': return 'bg-yellow-500';
      case 'adequate': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Supply Chain Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Monitor suppliers, orders, and inventory levels</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>New PO</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
              <p className="text-3xl font-bold text-gray-900">{supplyChainMetrics.totalSuppliers}</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-100">
              <Truck className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Orders</p>
              <p className="text-3xl font-bold text-gray-900">{supplyChainMetrics.activeOrders}</p>
            </div>
            <div className="p-3 rounded-xl bg-green-100">
              <Package className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Lead Time</p>
              <p className="text-3xl font-bold text-gray-900">{supplyChainMetrics.averageLeadTime}d</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-100">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">On-Time Delivery</p>
              <p className="text-3xl font-bold text-gray-900">{supplyChainMetrics.onTimeDelivery}%</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-3xl font-bold text-gray-900">${(supplyChainMetrics.totalValue / 1000).toFixed(0)}K</p>
            </div>
            <div className="p-3 rounded-xl bg-orange-100">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-3xl font-bold text-gray-900">{supplyChainMetrics.pendingApprovals}</p>
            </div>
            <div className="p-3 rounded-xl bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Purchase Orders */}
        <div className="lg:col-span-2">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Active Purchase Orders</h3>
                <p className="text-gray-600 text-sm">Track order status and delivery</p>
              </div>
              <div className="flex space-x-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Filter className="h-5 w-5 text-gray-500" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <RefreshCw className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {purchaseOrders.map((order, index) => (
                <div key={index} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{order.id}</h4>
                        <p className="text-sm text-gray-600">{order.supplier}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getPriorityColor(order.priority)}`}>
                        {order.priority}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Order Date</p>
                      <p className="font-medium">{new Date(order.orderDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Expected Delivery</p>
                      <p className="font-medium">{new Date(order.expectedDelivery).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Value</p>
                      <p className="font-medium">${order.value.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Items</p>
                      <p className="font-medium">{order.items} items</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <button className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1">
                      <Eye className="h-4 w-4" />
                      <span>View</span>
                    </button>
                    <button className="text-gray-600 hover:text-gray-700 text-sm font-medium flex items-center space-x-1">
                      <Edit className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Stock Alerts</h3>
              <p className="text-gray-600 text-sm">Items requiring attention</p>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</button>
          </div>
          
          <div className="space-y-4">
            {stockAlerts.map((alert, index) => (
              <div key={index} className="p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${getStockStatusColor(alert.status)}`} />
                    <h4 className="font-medium text-gray-900">{alert.medication}</h4>
                  </div>
                  <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">
                    {alert.status}
                  </span>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600">
                  <p>Current: {alert.currentStock} | Min: {alert.minimumLevel}</p>
                  <p>Supplier: {alert.supplier}</p>
                  <p>Lead Time: {alert.leadTime} days</p>
                </div>
                
                <button className="mt-3 w-full bg-red-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-red-700 transition-colors">
                  Reorder Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Delivery Performance */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Delivery Performance</h3>
              <p className="text-gray-600 text-sm">Monthly on-time delivery rates</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={deliveryPerformance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
              />
              <Line type="monotone" dataKey="onTime" stroke="#8B5CF6" strokeWidth={3} />
              <Line type="monotone" dataKey="late" stroke="#EF4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Supplier Performance */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Supplier Performance</h3>
              <p className="text-gray-600 text-sm">Performance ratings by supplier</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={supplierPerformance}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, performance }) => `${name} ${performance}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="performance"
              >
                {supplierPerformance.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, 'Performance']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Supplier Directory */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Supplier Directory</h3>
            <p className="text-gray-600 text-sm">Manage supplier relationships</p>
          </div>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
            Add Supplier
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {australianSuppliers.map((supplier) => (
            <div key={supplier.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">{supplier.name}</h4>
                  <p className="text-sm text-gray-600">{supplier.code}</p>
                </div>
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-sm font-medium">{supplier.rating}</span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>{supplier.address.split(',')[0]}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>{supplier.phone}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>{supplier.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>{supplier.leadTime} days lead time</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  supplier.preferredSupplier ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {supplier.preferredSupplier ? 'Preferred' : 'Standard'}
                </span>
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupplyChain;
